import type { IQueryPayload } from '@delasport/rule-engine-ui';
import type { IOption } from '@shared/form/types';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  FIELD_FILTERS_ALLOWED_MODULE,
  type IInitialFilter,
  type ISearchParameters,
  OPERATOR_SORT,
  OPERATOR_TYPE,
} from '@features/filters/types';
import { getFieldBasedPreviousFiltersInitialValues } from '@features/filters/utils/getFieldBasedFiltersInitialValues';
import { hasPartialUpdates } from '@utils/hasPartialUpdates';

interface IInitialState {
  searchParameters: ISearchParameters;
  prevFilters: Record<string, IInitialFilter>;
  dialogState: {
    isConfirmBrandsDialogOpen: boolean;
  };
  activeScheduledCampaign: {
    enabled: boolean;
    id: null | string;
    name: null | string;
  };
  scheduledCampaignConfig: {
    activeTab: number;
    criterion: string;
    queryError: boolean;
    isFormErrors: boolean;
    currentBrands: IOption[];
    previousBrands: IOption[];
    brandChangeReason: string;
    isBetTypesErrors: boolean;
    isSelectorChanged: boolean;
    segmentQuery?: IQueryPayload;
    hasBrandsWithoutAccess: boolean;
    isDirtyAdditionalConditions: boolean;
    isDisableSportTabDialogOpen: boolean;
    isAppearanceAndContentErrors: boolean;
    isDisableCasinoTabDialogOpen: boolean;
  };
}

export const initialScheduledCampaignsSearchParameters = {
  page: 1,
  size: 10,
  filters: [{ value: true, field: 'enabled', type: OPERATOR_TYPE.EQUAL }],
  sorts: [
    {
      field: 'modified_at',
      order: OPERATOR_SORT.DESC,
    },
  ],
};

const initialState: IInitialState = {
  dialogState: {
    isConfirmBrandsDialogOpen: false,
  },
  searchParameters: initialScheduledCampaignsSearchParameters,
  activeScheduledCampaign: {
    id: null,
    name: null,
    enabled: false,
  },
  prevFilters: getFieldBasedPreviousFiltersInitialValues(
    FIELD_FILTERS_ALLOWED_MODULE.SCHEDULED_CAMPAIGN
  ),
  scheduledCampaignConfig: {
    activeTab: 0,
    criterion: '',
    queryError: false,
    currentBrands: [],
    previousBrands: [],
    isFormErrors: false,
    brandChangeReason: '',
    segmentQuery: undefined,
    isBetTypesErrors: false,
    isSelectorChanged: false,
    hasBrandsWithoutAccess: false,
    isDirtyAdditionalConditions: false,
    isDisableSportTabDialogOpen: false,
    isAppearanceAndContentErrors: false,
    isDisableCasinoTabDialogOpen: false,
  },
};

const scheduledCampaignsSlice = createSlice({
  initialState,
  name: 'scheduledCampaignsSlice',
  reducers: {
    setPrevFilters(
      state: IInitialState,
      action: PayloadAction<Record<string, IInitialFilter>>
    ) {
      state.prevFilters = action.payload;
    },
    setActiveScheduledCampaign(
      state: IInitialState,
      action: PayloadAction<IInitialState['activeScheduledCampaign']>
    ) {
      state.activeScheduledCampaign = action.payload;
    },
    setScheduledCampaignDialogState(
      state: IInitialState,
      action: PayloadAction<Partial<IInitialState['dialogState']>>
    ) {
      state.dialogState = { ...state.dialogState, ...action.payload };
    },
    setSearchParameters(
      state: IInitialState,
      action: PayloadAction<Partial<ISearchParameters>>
    ) {
      if (!hasPartialUpdates(state.searchParameters, action.payload)) {
        return;
      }

      state.searchParameters = { ...state.searchParameters, ...action.payload };
    },
    setScheduledCampaignConfig(
      state: IInitialState,
      action: PayloadAction<Partial<IInitialState['scheduledCampaignConfig']>>
    ) {
      if (!hasPartialUpdates(state.scheduledCampaignConfig, action.payload)) {
        return;
      }

      Object.assign(state.scheduledCampaignConfig, action.payload);
    },
  },
});

export default scheduledCampaignsSlice.reducer;

export const {
  setPrevFilters,
  setSearchParameters,
  setScheduledCampaignConfig,
  setActiveScheduledCampaign,
  setScheduledCampaignDialogState,
} = scheduledCampaignsSlice.actions;
